/**
 * `roboto://ui/time-format?format=<fmt>` is a link the AI writes to hand the
 * user a one-click switch of their timestamp display format, instead of
 * restating timestamps itself.
 *
 * `ui` is not a `RobotoUriType`, so `parseRobotoUri` already declines this URL
 * and no resource-link path can mistake it for an entity reference.
 */

import { isRobotoUri } from "@/shared/components/RobotoMarkdown/magicLinks";
import {
  isTimestampFormat,
  type TimestampFormat,
} from "@/shared/components/RobotoMarkdown/timeFormat";

/**
 * Extract the target {@link TimestampFormat} from a
 * `roboto://ui/time-format?format=<fmt>` URL, or `undefined` if it isn't one.
 *
 * An unrecognized `format` is the model inventing one, so it resolves to
 * `undefined` rather than a default — falling back would apply a change the
 * user's click never asked for.
 */
export const parseTimeFormatSettingLink = (
  url: string,
): TimestampFormat | undefined => {
  // Cheap scheme guard first: this runs for every link in every AI response,
  // and all but a handful exit here without constructing a URL.
  if (!isRobotoUri(url)) {
    return undefined;
  }
  try {
    const parsed = new URL(url);
    if (parsed.hostname !== "ui" || parsed.pathname !== "/time-format") {
      return undefined;
    }
    // `isTimestampFormat` takes `unknown`, so it also rejects a missing param.
    const format = parsed.searchParams.get("format");
    return isTimestampFormat(format) ? format : undefined;
  } catch {
    return undefined;
  }
};
